import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../../../lib/supabase';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine
} from 'recharts';

interface Worker {
  id: string;
  name: string;
  email: string;
  qualifications: string[];
  created_at: string;
}

interface Evaluation {
  id: string;
  worker_id: string;
  顧客目線_rating: number;
  笑顔_rating: number;
  園の方針_rating: number;
  事前準備_rating: number;
  雰囲気_rating: number;
  運資質_rating: number;
  清潔感_rating: number;
  態度_rating: number;
  work_date: string;
  overall_comment: string;
  evaluator_name: string;
  created_at: string;
}

interface Onboarding {
  id: string;
  worker_id: string;
  commute_method: string;
  created_at: string;
}

const categories = [
  { key: '顧客目線_rating', label: '顧客目線' },
  { key: '笑顔_rating', label: '笑顔' },
  { key: '園の方針_rating', label: '園の方針' },
  { key: '事前準備_rating', label: '事前準備' },
  { key: '雰囲気_rating', label: '雰囲気' },
  { key: '運資質_rating', label: '運資質' },
  { key: '清潔感_rating', label: '清潔感' },
  { key: '態度_rating', label: '態度' }
];

const historyColors = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];

// スライダーコンポーネント
const RatingSlider = ({
  label,
  value,
  onChange
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) => {
  const getColor = (v: number) => {
    if (v <= 2) return '#ef4444';
    if (v <= 4) return '#f97316';
    if (v <= 6) return '#eab308';
    if (v <= 8) return '#22c55e';
    return '#6366f1';
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span
          className="text-base font-bold w-10 text-right"
          style={{ color: getColor(value) }}
        >
          {value.toFixed(1)}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-400 w-4">1</span>
        <input
          type="range"
          min={1}
          max={10}
          step={0.5}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="flex-1 h-2 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${getColor(value)} 0%, ${getColor(value)} ${(value - 1) / 9 * 100}%, #e2e8f0 ${(value - 1) / 9 * 100}%, #e2e8f0 100%)`
          }}
        />
        <span className="text-xs text-slate-400 w-6">10</span>
      </div>
    </div>
  );
};

const WorkerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [worker, setWorker] = useState<Worker | null>(null);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [chartMode, setChartMode] = useState<'average' | 'history'>('history');
  const [showNewEvalForm, setShowNewEvalForm] = useState(false);
  const [editingEval, setEditingEval] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(true);

  const [newEval, setNewEval] = useState({
    work_date: new Date().toISOString().split('T')[0],
    顧客目線_rating: 5,
    笑顔_rating: 5,
    園の方針_rating: 5,
    事前準備_rating: 5,
    雰囲気_rating: 5,
    運資質_rating: 5,
    清潔感_rating: 5,
    態度_rating: 5,
    overall_comment: '',
    evaluator_name: ''
  });

  const fetchWorkerData = async () => {
    if (!id) return;
    setLoading(true);

    try {
      const { data: workerData, error: workerError } = await supabase
        .from('workers')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (workerError) throw workerError;
      if (workerData) setWorker(workerData);

      const { data: evalsData, error: evalsError } = await supabase
        .from('worker_evaluations')
        .select('*')
        .eq('worker_id', id)
        .order('work_date', { ascending: false });

      if (evalsError) throw evalsError;
      if (evalsData) setEvaluations(evalsData);

      const { data: onboardingData, error: onboardingError } = await supabase
        .from('worker_onboardings')
        .select('*')
        .eq('worker_id', id)
        .maybeSingle();

      if (onboardingError) throw onboardingError;
      if (onboardingData) setOnboarding(onboardingData);
    } catch (error) {
      console.error('Error fetching worker data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkerData();
  }, [id]);

  const handleAddEvaluation = async () => {
    if (!id) return;

    try {
      const { error } = await supabase.from('worker_evaluations').insert([
        {
          worker_id: id,
          ...newEval
        }
      ]);

      if (error) throw error;

      await fetchWorkerData();
      setShowNewEvalForm(false);
      setNewEval({
        work_date: new Date().toISOString().split('T')[0],
        顧客目線_rating: 5,
        笑顔_rating: 5,
        園の方針_rating: 5,
        事前準備_rating: 5,
        雰囲気_rating: 5,
        運資質_rating: 5,
        清潔感_rating: 5,
        態度_rating: 5,
        overall_comment: '',
        evaluator_name: ''
      });
    } catch (error) {
      console.error('Error adding evaluation:', error);
      alert('評価の追加に失敗しました');
    }
  };

  const handleEditEvaluation = async () => {
    if (!editingEval) return;

    try {
      const { error } = await supabase
        .from('worker_evaluations')
        .update({
          顧客目線_rating: editingEval.顧客目線_rating,
          笑顔_rating: editingEval.笑顔_rating,
          園の方針_rating: editingEval.園の方針_rating,
          事前準備_rating: editingEval.事前準備_rating,
          雰囲気_rating: editingEval.雰囲気_rating,
          運資質_rating: editingEval.運資質_rating,
          清潔感_rating: editingEval.清潔感_rating,
          態度_rating: editingEval.態度_rating,
          work_date: editingEval.work_date,
          overall_comment: editingEval.overall_comment,
          evaluator_name: editingEval.evaluator_name
        })
        .eq('id', editingEval.id);

      if (error) throw error;

      await fetchWorkerData();
      setEditingEval(null);
    } catch (error) {
      console.error('Error updating evaluation:', error);
      alert('評価の更新に失敗しました');
    }
  };

  // ランク判定ロジック（モックと統一）
  const calculateRank = (score: number, evalCount: number): string => {
    if (evalCount === 0) return '未評価';
    if (score >= 60) return 'S';
    if (score >= 50) return 'A';
    if (score >= 40) return 'B';
    return 'ブロック';
  };

  // 平均スコア計算（10点満点ベース）
  const calculateAverageScore = (): number => {
    if (evaluations.length === 0) return 0;
    const totalScore = evaluations.reduce((sum, ev) => {
      const evalTotal = categories.reduce(
        (catSum, cat) => catSum + ((ev[cat.key as keyof Evaluation] as number) || 5),
        0
      );
      return sum + evalTotal;
    }, 0);
    return totalScore / evaluations.length;
  };

  const averageScore = calculateAverageScore();
  const rank = calculateRank(averageScore, evaluations.length);

  // 成長中/下降中判定
  const getTrendBadge = () => {
    if (evaluations.length < 2) return null;
    const recent = evaluations.slice(0, 3);
    const recentScores = recent.map(ev =>
      categories.reduce((sum, cat) => sum + ((ev[cat.key as keyof Evaluation] as number) || 5) * 2, 0)
    );
    const isGrowing = recentScores[0] > recentScores[recentScores.length - 1];
    return isGrowing ? (
      <span className="ml-3 px-3 py-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm rounded-full font-medium shadow-md">
        📈 成長中
      </span>
    ) : (
      <span className="ml-3 px-3 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm rounded-full font-medium shadow-md">
        📉 下降中
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-7xl mb-6">⏳</div>
          <h2 className="text-3xl font-bold text-slate-800">読み込み中...</h2>
        </div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-7xl mb-6">🔍</div>
          <h2 className="text-3xl font-bold text-slate-800 mb-4">ワーカーが見つかりません</h2>
          <Link
            to="/admin/workers"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white text-lg rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            ワーカー一覧へ戻る
          </Link>
        </div>
      </div>
    );
  }

  // 平均データ（RadarChart用）
  const averageData = categories.map(cat => {
    const sum = evaluations.reduce((acc, ev) => acc + ((ev[cat.key as keyof Evaluation] as number) || 5), 0);
    const avg = evaluations.length > 0 ? sum / evaluations.length : 5;
    return { category: cat.label, value: parseFloat(avg.toFixed(1)) };
  });

  // 履歴データ（RadarChart用）
  const historyRadarData = categories.map(cat => {
    const entry: Record<string, string | number> = { category: cat.label };
    evaluations.slice(0, 6).forEach((ev, idx) => {
      entry[`ev${idx}`] = (ev[cat.key as keyof Evaluation] as number) || 5;
    });
    return entry;
  });

  // 成長推移データ
  const growthData = evaluations
    .slice()
    .reverse()
    .map(ev => {
      const total = categories.reduce((sum, cat) => sum + ((ev[cat.key as keyof Evaluation] as number) || 5), 0);
      return {
        date: new Date(ev.work_date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }),
        score: parseFloat(total.toFixed(1))
      };
    });

  const getRankBadgeColor = (rank: string) => {
    switch (rank) {
      case 'S': return 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white';
      case 'A': return 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white';
      case 'B': return 'bg-gradient-to-r from-slate-400 to-slate-500 text-white';
      case 'ブロック': return 'bg-gradient-to-r from-red-500 to-red-700 text-white';
      case '未評価': return 'bg-gradient-to-r from-slate-200 to-slate-300 text-slate-600';
      default: return 'bg-slate-200 text-slate-700';
    }
  };

  const displayedEvaluations = showAllHistory ? evaluations : evaluations.slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* ヘッダー */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/workers"
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all text-base"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                ワーカー一覧へ戻る
              </Link>
              <div className="h-8 w-px bg-slate-300"></div>
              <h1 className="text-2xl font-bold text-slate-800">ワーカー詳細</h1>
            </div>
            {evaluations.length > 0 && (
              <Link
                to={`/admin/workers/${id}/offer`}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-base font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-mail-send-line text-xl"></i>
                オファーメールを送信
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* ワーカー基本情報 */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-slate-200">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                {worker.name.charAt(0)}
              </div>
              <div>
                <h2 className="text-3xl font-bold text-slate-800 mb-4">{worker.name}</h2>
                <p className="text-lg text-slate-600 mb-3">{worker.email}</p>
                <div className="flex items-center gap-3">
                  {worker.qualifications?.map((qual, idx) => (
                    <span key={idx} className="px-4 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-base rounded-full font-medium shadow-md">
                      {qual}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className={`px-6 py-3 rounded-xl text-2xl font-bold shadow-lg ${getRankBadgeColor(rank)}`}>
              {rank}ランク
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 pt-6 border-t border-slate-200">
            <div className="text-center">
              <div className="text-base text-slate-600 mb-2">通勤手段</div>
              <div className="text-xl font-bold text-slate-800">{onboarding?.commute_method || '未登録'}</div>
            </div>
            <div className="text-center">
              <div className="text-base text-slate-600 mb-2">登録日</div>
              <div className="text-xl font-bold text-slate-800">
                {new Date(worker.created_at).toLocaleDateString('ja-JP')}
              </div>
            </div>
            <div className="text-center">
              <div className="text-base text-slate-600 mb-2">評価回数</div>
              <div className="text-xl font-bold text-slate-800">{evaluations.length}回</div>
            </div>
          </div>
        </div>

        {/* 平均スコアとランク判定 */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-3">
              <i className="ri-bar-chart-box-line text-indigo-600 text-2xl"></i>
              平均スコア
            </h3>
            <div className="text-center">
              <div className="text-6xl font-bold text-indigo-600 mb-3">
                {averageScore.toFixed(1)}
              </div>
              <div className="text-lg text-slate-600">/ 80.0点</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-3">
              <i className="ri-trophy-line text-indigo-600 text-2xl"></i>
              ランク基準
            </h3>
            <div className="space-y-3">
              <div className="flex items-start justify-between p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                <div>
                  <span className="font-bold text-yellow-700 text-base block">Sランク</span>
                  <span className="text-xs text-yellow-600 mt-0.5 block">即直接雇用・最適な人材幹部候補</span>
                </div>
                <span className="text-slate-700 text-base font-semibold whitespace-nowrap ml-4 mt-0.5">60点以上</span>
              </div>
              <div className="flex items-start justify-between p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                <div>
                  <span className="font-bold text-emerald-700 text-base block">Aランク</span>
                  <span className="text-xs text-emerald-600 mt-0.5 block">直接スポットへ招待</span>
                </div>
                <span className="text-slate-700 text-base font-semibold whitespace-nowrap ml-4 mt-0.5">50点以上</span>
              </div>
              <div className="flex items-start justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <span className="font-bold text-slate-700 text-base block">Bランク</span>
                  <span className="text-xs text-slate-500 mt-0.5 block">タイムリー継続</span>
                </div>
                <span className="text-slate-700 text-base font-semibold whitespace-nowrap ml-4 mt-0.5">40点以上</span>
              </div>
              <div className="flex items-start justify-between p-3 bg-gradient-to-r from-red-50 to-rose-50 rounded-lg border border-red-200">
                <div>
                  <span className="font-bold text-red-700 text-base block">ブロック</span>
                  <span className="text-xs text-red-500 mt-0.5 block">オファー不可・要注意</span>
                </div>
                <span className="text-slate-700 text-base font-semibold whitespace-nowrap ml-4 mt-0.5">39点以下</span>
              </div>
            </div>
          </div>
        </div>

        {/* レーダーチャートと成長推移 */}
        {evaluations.length > 0 && (
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-800 flex items-center gap-3">
                  <i className="ri-radar-line text-indigo-600 text-2xl"></i>
                  評価レーダーチャート
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setChartMode('average')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      chartMode === 'average'
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    平均
                  </button>
                  <button
                    onClick={() => setChartMode('history')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      chartMode === 'history'
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    履歴重ね
                  </button>
                </div>
              </div>

              {chartMode === 'average' ? (
                <ResponsiveContainer width="100%" height={380}>
                  <RadarChart data={averageData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="category" tick={{ fill: '#475569', fontSize: 13 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 11 }} />
                    <Radar
                      name="平均"
                      dataKey="value"
                      stroke="#6366f1"
                      fill="#6366f1"
                      fillOpacity={0.55}
                    />
                    <Legend wrapperStyle={{ fontSize: '13px' }} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={320}>
                    <RadarChart data={historyRadarData}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="category" tick={{ fill: '#475569', fontSize: 13 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 11 }} />
                      {evaluations.slice(0, 6).map((ev, idx) => {
                        const color = historyColors[idx % historyColors.length];
                        return (
                          <Radar
                            key={ev.id}
                            name={new Date(ev.work_date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
                            dataKey={`ev${idx}`}
                            stroke={color}
                            fill={color}
                            fillOpacity={0.18}
                            strokeWidth={2}
                          />
                        );
                      })}
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                    </RadarChart>
                  </ResponsiveContainer>
                  <div className="mt-2 flex flex-wrap gap-2 justify-center">
                    {evaluations.slice(0, 6).map((ev, idx) => (
                      <span
                        key={ev.id}
                        className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full"
                        style={{ backgroundColor: `${historyColors[idx % historyColors.length]}20`, color: historyColors[idx % historyColors.length], border: `1px solid ${historyColors[idx % historyColors.length]}60` }}
                      >
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: historyColors[idx % historyColors.length] }}></span>
                        {new Date(ev.work_date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                <i className="ri-line-chart-line text-indigo-600 text-2xl"></i>
                成長推移
                {getTrendBadge()}
              </h3>
              <ResponsiveContainer width="100%" height={380}>
                <LineChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 13 }} />
                  <YAxis domain={[0, 80]} tick={{ fill: '#64748b', fontSize: 13 }} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <ReferenceLine y={60} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'S基準', position: 'right', fill: '#f59e0b', fontSize: 12 }} />
                  <ReferenceLine y={50} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'A基準', position: 'right', fill: '#10b981', fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#6366f1"
                    strokeWidth={3}
                    dot={{ fill: '#6366f1', r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* 新規評価追加ボタン */}
        <div className="mb-8">
          <button
            onClick={() => setShowNewEvalForm(!showNewEvalForm)}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-lg font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            <i className={`text-2xl ${showNewEvalForm ? 'ri-close-line' : 'ri-add-line'}`}></i>
            {showNewEvalForm ? '評価フォームを閉じる' : '新しい評価を追加'}
          </button>
        </div>

        {/* 新規評価フォーム */}
        {showNewEvalForm && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-slate-200">
            <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
              <i className="ri-file-add-line text-emerald-600 text-2xl"></i>
              新規評価を追加
            </h3>
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-base font-medium text-slate-700 mb-2">勤務日</label>
                <input
                  type="date"
                  value={newEval.work_date}
                  onChange={(e) => setNewEval({ ...newEval, work_date: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-slate-700 mb-2">評価者名</label>
                <input
                  type="text"
                  value={newEval.evaluator_name}
                  onChange={(e) => setNewEval({ ...newEval, evaluator_name: e.target.value })}
                  placeholder="例: 園長先生"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                />
              </div>
            </div>

            {/* スライダー評価 */}
            <div className="bg-slate-50 rounded-xl p-6 mb-6 border border-slate-200">
              <div className="text-base font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <i className="ri-sliders-line text-indigo-600"></i>
                各項目の評価（1〜10）
              </div>
              <div className="grid grid-cols-2 gap-x-10 gap-y-2">
                {categories.map(cat => (
                  <RatingSlider
                    key={cat.key}
                    label={cat.label}
                    value={newEval[cat.key as keyof typeof newEval] as number}
                    onChange={(v) => setNewEval({ ...newEval, [cat.key]: v })}
                  />
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-base font-medium text-slate-700 mb-2">総合コメント</label>
              <textarea
                value={newEval.overall_comment}
                onChange={(e) => setNewEval({ ...newEval, overall_comment: e.target.value })}
                placeholder="総合的な評価コメントを入力してください"
                rows={4}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
              />
            </div>

            <button
              onClick={handleAddEvaluation}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-lg font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              <i className="ri-save-line text-2xl"></i>
              評価を保存
            </button>
          </div>
        )}

        {/* 評価履歴 */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
            <i className="ri-history-line text-indigo-600 text-2xl"></i>
            評価履歴（{evaluations.length}件）
          </h3>

          {evaluations.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <p className="text-xl text-slate-600">まだ評価がありません</p>
            </div>
          ) : (
            <>
              <div className="space-y-6">
                {displayedEvaluations.map((ev) => {
                  const evalData = categories.map(cat => ({
                    category: cat.label,
                    value: ((ev[cat.key as keyof Evaluation] as number) || 5) * 2
                  }));

                  return (
                    <div key={ev.id} className="border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="text-lg font-bold text-slate-800 mb-1">
                            {new Date(ev.work_date).toLocaleDateString('ja-JP', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                          <div className="text-base text-slate-600">評価者: {ev.evaluator_name}</div>
                        </div>
                        <button
                          onClick={() => setEditingEval(ev)}
                          className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 text-sm rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-edit-line"></i>
                          編集
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          {(() => {
                            const evalIndex = evaluations.findIndex(e => e.id === ev.id);
                            const color = historyColors[evalIndex % historyColors.length];
                            const evalData = categories.map(cat => ({
                              category: cat.label,
                              value: ((ev[cat.key as keyof Evaluation] as number) || 5) * 2
                            }));
                            return (
                              <ResponsiveContainer width="100%" height={280}>
                                <RadarChart data={evalData}>
                                  <PolarGrid stroke="#e2e8f0" />
                                  <PolarAngleAxis dataKey="category" tick={{ fill: '#475569', fontSize: 12 }} />
                                  <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 11 }} />
                                  <Radar
                                    dataKey="value"
                                    stroke={color}
                                    fill={color}
                                    fillOpacity={0.45}
                                    strokeWidth={2}
                                  />
                                </RadarChart>
                              </ResponsiveContainer>
                            );
                          })()}
                        </div>

                        <div>
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            {categories.map(cat => (
                              <div key={cat.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <span className="text-sm text-slate-700">{cat.label}</span>
                                <span className="text-base font-bold text-indigo-600">
                                  {ev[cat.key as keyof Evaluation]}/10
                                </span>
                              </div>
                            ))}
                          </div>
                          {ev.overall_comment && (
                            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                              <div className="text-sm font-medium text-indigo-900 mb-2">総合コメント</div>
                              <p className="text-base text-indigo-800">{ev.overall_comment}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {evaluations.length > 3 && (
                <button
                  onClick={() => setShowAllHistory(!showAllHistory)}
                  className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 text-base font-medium rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap"
                >
                  {showAllHistory ? (
                    <><i className="ri-arrow-up-line"></i>一部のみ表示</>
                  ) : (
                    <><i className="ri-arrow-down-line"></i>すべて表示（{evaluations.length}件）</>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 編集モーダル */}
      {editingEval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <i className="ri-edit-line text-indigo-600 text-2xl"></i>
                評価を編集
              </h3>
              <button
                onClick={() => setEditingEval(null)}
                className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-base font-medium text-slate-700 mb-2">勤務日</label>
                  <input
                    type="date"
                    value={editingEval.work_date}
                    onChange={(e) => setEditingEval({ ...editingEval, work_date: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-slate-700 mb-2">評価者名</label>
                  <input
                    type="text"
                    value={editingEval.evaluator_name}
                    onChange={(e) => setEditingEval({ ...editingEval, evaluator_name: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                  />
                </div>
              </div>

              {/* スライダー評価（編集） */}
              <div className="bg-slate-50 rounded-xl p-6 mb-6 border border-slate-200">
                <div className="text-base font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <i className="ri-sliders-line text-indigo-600"></i>
                  各項目の評価（1〜10）
                </div>
                <div className="grid grid-cols-2 gap-x-10 gap-y-2">
                  {categories.map(cat => (
                    <RatingSlider
                      key={cat.key}
                      label={cat.label}
                      value={editingEval[cat.key as keyof Evaluation] as number}
                      onChange={(v) => setEditingEval({ ...editingEval, [cat.key]: v })}
                    />
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-base font-medium text-slate-700 mb-2">総合コメント</label>
                <textarea
                  value={editingEval.overall_comment}
                  onChange={(e) => setEditingEval({ ...editingEval, overall_comment: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setEditingEval(null)}
                  className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 text-base font-medium rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleEditEvaluation}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-base font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-save-line text-xl"></i>
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerDetailPage;